import { NextSeo } from 'next-seo';
import { FC, useMemo } from 'react';

import { useLecturePageQueryParams } from '@/components/ui/features/lecture/hooks';
import { queryThinLectureWithTagAndEvent } from '@/fetchers/thinLectures';
import { useResource } from '@/lib/datx-jsonapi-react';
import { getScholarTagsForLectureId } from '@/queries/google-scholar-tags';
import { GoogleScholarTags } from '@/resources/GoogleScholarTags';

function getAdditionalMetaTags(scholarMetaTags: GoogleScholarTags) {
  const additionalMetaTags = [
    { name: 'citation_title', content: scholarMetaTags.citationTitle },
    { name: 'citation_publication_date', content: scholarMetaTags.citationPublicationDate },
    { name: 'citation_conference_title', content: scholarMetaTags.citationConferenceTitle },
    { name: 'citation_doi', content: scholarMetaTags.citationDoi },
    { name: 'citation_abstract', content: scholarMetaTags.citationAbstract },
    ...generateCitationAuthorTags(scholarMetaTags),
  ];

  return additionalMetaTags.filter((amt) => amt.content);
}

function generateCitationAuthorTags(scholarMetaTags: GoogleScholarTags) {
  return scholarMetaTags.citationAuthors.map((ca) => ({ name: 'citation_author', content: ca }));
}

export const LecturePageSeo: FC = () => {
  const { id: lectureId } = useLecturePageQueryParams();
  const { data: lecture } = useResource(queryThinLectureWithTagAndEvent(lectureId));
  const { data: scholarMetaTags } = useResource(getScholarTagsForLectureId(lectureId));
  const additionalMetaTags = useMemo(() => getAdditionalMetaTags(scholarMetaTags), [scholarMetaTags]);

  if (!lecture) {
    return null;
  }

  return (
    <NextSeo
      title={lecture.title}
      noindex={!lecture.library}
      canonical={`${process.env.NEXT_PUBLIC_BASE_URL}lecture/${lecture.linkSlug}`}
      additionalMetaTags={additionalMetaTags}
      openGraph={{
        url: `${process.env.NEXT_PUBLIC_BASE_URL}lecture/${lecture.linkSlug}`,
        title: lecture.title,
        images: [
          {
            url: lecture.posterLecture
              ? lecture.largePosterDocumentThumbnailUrl || lecture.largePosterDocumentThumbnailExtractUrl
              : lecture.mediumPosterUrl || `${process.env.NEXT_PUBLIC_BASE_URL}images/landing/hero-share.jpg`,
            width: 800,
            height: 600,
            alt: lecture.title,
          },
        ],
      }}
    />
  );
};
